import {createAction} from 'typesafe-actions';

import {
  type PanelBankFlowSectionConfig,
  type PanelConfigOverride,
  type PanelPlacementOverride,
  type SectionPanelSorting,
} from '../../../components/PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../components/PanelMediaBrowser/common';
import {type LinePlotSettings} from '../../../components/WorkspaceDrawer/Settings/types';
import {type GridLayout} from '../../../util/panelbankGrid';
import {type LayedOutPanel} from '../../../util/panelTypes';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankConfigTypes from '../panelBankConfig/types';
import type * as WorkspaceSettingsTypes from '../workspaceSettings/types';
import type * as Types from './types';

export type PanelConfigPatch = {
  values: Partial<PanelTypes.Panel['config']>;
  removedKeys: string[];
};

// Toggle between 'flow' and 'grid' modes
export const toggleType = createAction(
  '@view/panelBankSectionConfig/toggleType',
  action => (ref: Types.Ref) => action({ref})
);

// Toggle expanded/collapsed state
export const toggleIsOpen = createAction(
  '@view/panelBankSectionConfig/toggleIsOpen',
  action => (ref: Types.Ref) => action({ref})
);

// Rename the section
export const updateName = createAction(
  '@view/panelBankSectionConfig/updateName',
  action =>
    (
      ref: Types.Ref,
      newName: string,
      isPanelsAutoForUndoRedo?: {value: boolean | undefined},
      /**
       * for undo, prev panel.isAuto vals must be provided to allow setting
       * panels back to their original values before the section was renamed
       */
      panelAutoVals?: Array<boolean | undefined>
    ) =>
      action({ref, newName, isPanelsAutoForUndoRedo, panelAutoVals})
);

// Handles box-resizing and changing rows per page in flow sections ('standard layout')
export const updateFlowConfig = createAction(
  '@view/panelBankSectionConfig/updateFlowConfig',
  action =>
    (
      ref: Types.Ref,
      newFlowConfig?: Partial<PanelBankFlowSectionConfig>,
      /**
       * whether to PATCH (merge changes) or PUT (replace whole config);
       * PUT is required on undo in order to revert values to undefined
       */
      operation: 'PATCH' | 'PUT' = 'PATCH'
    ) =>
      action({ref, newFlowConfig, operation})
);

// Handles saving box layout in grid sections ('custom layout')
export const setGridLayout = createAction(
  '@view/panelBankSectionConfig/setGridLayout',
  action => (ref: Types.Ref, newGridLayout: GridLayout) =>
    action({ref, newGridLayout})
);

// Add a panel to this section by ref
export const addPanelByRef = createAction(
  '@view/panelBankSectionConfig/addPanelByRef',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref, // the panel we're adding
      opts?: {
        shouldAlphaInsert?: boolean;
        fatPanel?: boolean;
        callbackFn?: (
          panel: LayedOutPanel,
          newPanelRef: PanelTypes.Ref
        ) => void;
        panelBankConfigRef?: PanelBankConfigTypes.Ref;
      }
    ) =>
      action({
        ref,
        panelRef,
        shouldAlphaInsert: opts?.shouldAlphaInsert,
        fatPanel: opts?.fatPanel,
        callbackFn: opts?.callbackFn,
        panelBankConfigRef: opts?.panelBankConfigRef,
      })
);

// Add a panel to this section by panel config
export const addPanelByConfig = createAction(
  '@view/panelBankSectionConfig/addPanelByConfig',
  action =>
    (
      ref: Types.Ref,
      panel: LayedOutPanel, // the panel we're adding
      opts?: {
        shouldAlphaInsert?: boolean;
        fatPanel?: boolean;
        callbackFn?: (
          panel: LayedOutPanel,
          newPanelRef: PanelTypes.Ref
        ) => void;
        panelBankConfigRef?: PanelBankConfigTypes.Ref;
      }
    ) =>
      action({
        ref,
        panel,
        shouldAlphaInsert: opts?.shouldAlphaInsert,
        fatPanel: opts?.fatPanel,
        callbackFn: opts?.callbackFn,
        panelBankConfigRef: opts?.panelBankConfigRef,
      })
);

// Add multiple panels to this section by panel config
export const addPanelsByConfig = createAction(
  '@view/panelBankSectionConfig/addPanelsByConfig',
  action =>
    (
      ref: Types.Ref,
      panels: LayedOutPanel[],
      opts?: {
        shouldAlphaInsert?: boolean;
        panelBankConfigRef?: PanelBankConfigTypes.Ref;
      }
    ) =>
      action({
        ref,
        panels,
        shouldAlphaInsert: opts?.shouldAlphaInsert,
        panelBankConfigRef: opts?.panelBankConfigRef,
      })
);

export const deletePanel = createAction(
  '@view/panelBankSectionConfig/deletePanel',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref, // the panel we're deleting
      panelBankConfigRef?: PanelBankConfigTypes.Ref, // the panel bank config ref (optional, only neccessary if we're dealing with hidden panels)
      workspaceSettingsRef?: WorkspaceSettingsTypes.Ref, // to check panel auto-gen setting; don't need to use hidden panels if auto-gen off
      isPanelsAutoForUndoRedo?: {value: boolean | undefined}
    ) =>
      action({
        ref,
        panelRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo,
      })
);

export const duplicatePanel = createAction(
  '@view/panelBankSectionConfig/duplicatePanel',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref, // the panel we're duplicating
      panelBankConfigRef?: PanelBankConfigTypes.Ref
    ) =>
      action({ref, panelRef, panelBankConfigRef})
);

export const sortPanels = createAction(
  '@view/panelBankSectionConfig/sortPanels',
  action =>
    (refs: Types.Ref[], panelBankConfigRef?: PanelBankConfigTypes.Ref) =>
      action({refs, panelBankConfigRef})
);

export const setSectionPanelRefsAndUndoSortingSetting = createAction(
  '@view/panelBankSectionConfig/setSectionPanelRefsAndUndoSortingSetting',
  action =>
    (
      refs: Types.Ref[],
      orderedPanelRefs: PanelTypes.Ref[][],
      sectionSortings: SectionPanelSorting[],
      panelBankConfigRef?: PanelBankConfigTypes.Ref,
      prevOverrides?: Record<string, PanelPlacementOverride>
    ) =>
      action({
        refs,
        orderedPanelRefs,
        sectionSortings,
        panelBankConfigRef,
        prevOverrides,
      })
);

export const insertUpdatedPanel = createAction(
  '@view/panelBankSectionConfig/insertUpdatedPanel',
  action =>
    (
      ref: Types.Ref,
      fromPanelRef: PanelTypes.Ref,
      panelRef: PanelTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref
    ) =>
      action({ref, fromPanelRef, panelRef, panelBankConfigRef})
);

export const applyCopiedPanelConfig = createAction(
  '@view/panelBankSectionConfig/applyCopiedPanelConfig',
  action =>
    (
      ref: Types.Ref,
      fromPanelRef: PanelTypes.Ref,
      panelRef: PanelTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref
    ) =>
      action({ref, fromPanelRef, panelRef, panelBankConfigRef})
);

export const undoApplyCopiedPanelConfig = createAction(
  '@view/panelBankSectionConfig/undoApplyCopiedPanelConfig',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref,
      configPatch: PanelConfigPatch,
      key: string | undefined,
      isAuto: boolean | undefined,
      isPanelsAuto: boolean | undefined,
      overrideInfo?: {
        panelBankConfigRef: PanelBankConfigTypes.Ref;
        panelKey: string;
        prevOverride: PanelConfigOverride | undefined;
      }
    ) =>
      action({
        ref,
        panelRef,
        configPatch,
        key,
        isAuto,
        isPanelsAuto,
        overrideInfo,
      })
);

export const undoInsertUpdatedPanel = createAction(
  '@view/panelBankSectionConfig/undoInsertUpdatedPanel',
  action =>
    (
      ref: Types.Ref,
      panel: PanelTypes.Panel,
      panelRef: PanelTypes.Ref,
      isPanelsAuto: boolean | undefined,
      overrideInfo?: {
        panelBankConfigRef: PanelBankConfigTypes.Ref;
        panelKey: string;
        prevOverride: PanelConfigOverride | undefined;
      }
    ) =>
      action({ref, panel, panelRef, isPanelsAuto, overrideInfo})
);

export const updateLinePlotSectionSettings = createAction(
  '@view/sectionSettings/linePlot/update',
  action => (ref: Types.Ref, settings: Partial<LinePlotSettings> | undefined) =>
    action({ref, settings})
);

export const updateSectionMediaSettings = createAction(
  '@view/sectionSettings/media/update',
  action =>
    (ref: Types.Ref, settings: Partial<MediaBrowserPanelConfig> | undefined) =>
      action({ref, settings})
);

export const unsetSectionMediaSetting = createAction(
  '@view/sectionSettings/media/unset',
  action => (ref: Types.Ref, field: keyof MediaBrowserPanelConfig) =>
    action({ref, field})
);
